import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'motion/react';

import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import { PRACTICE_LIST } from '../ui/practiceListTheme';

export const LANDING_TONE = {
  ready: PRACTICE_LIST.ready,
  done: PRACTICE_LIST.completed,
  quiet: PRACTICE_LIST.unavailable,
};

const sectionCaptionSx = {
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: LANDING_TONE.ready.accent,
};

export const landingCaptionSx = sectionCaptionSx;

export const LandingRow = ({ mark: MarkIcon, tone = 'quiet', title, label, body, titleComponent = 'h3' }) => {
  const accent = (LANDING_TONE[tone] || LANDING_TONE.quiet).accent;

  return (
    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
      <Box sx={{ color: accent, lineHeight: 0, mt: '2px', flex: '0 0 auto' }}>
        <MarkIcon aria-hidden="true" sx={{ fontSize: 28 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <LandingTitle component={titleComponent}>{title}</LandingTitle>
        {label ? <LandingLabel accent={accent}>{label}</LandingLabel> : null}
        {body ? <LandingBody>{body}</LandingBody> : null}
      </Box>
    </Box>
  );
};

export const LandingCard = ({
  mark: MarkIcon,
  tone = 'quiet',
  title,
  label,
  body,
  titleComponent = 'h3',
  kind = 'choice',
}) => {
  const colors = LANDING_TONE[tone] || LANDING_TONE.quiet;
  const filled = kind === 'choice' || (kind === 'price' && tone === 'done');
  const ruled = kind === 'choice' || kind === 'audience' || kind === 'price';
  const inline = kind === 'dilemma' || kind === 'asset';
  const banner = kind === 'price' && tone === 'done';
  const iconSize = kind === 'choice' ? 32 : kind === 'dilemma' ? 22 : 26;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: inline ? '20px' : '24px',
        py: inline ? '20px' : '24px',
        borderRadius: '16px',
        bgcolor: filled ? colors.soft : 'background.paper',
        border: '1px solid',
        borderColor: filled ? 'transparent' : 'divider',
        ...(ruled ? { borderTop: `3px solid ${colors.accent}` } : null),
      }}
    >
      {inline ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, color: colors.accent }}>
          <MarkIcon aria-hidden="true" sx={{ fontSize: iconSize }} />
          <LandingTitle
            component={titleComponent}
            sx={kind === 'dilemma'
              ? {
                fontSize: '0.8rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: colors.accent,
              }
              : { fontSize: '1.05rem' }}
          >
            {title}
          </LandingTitle>
        </Box>
      ) : (
        <Box
          sx={banner
            ? {
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'flex-start' },
              gap: { xs: 2, sm: 3 },
            }
            : null}
        >
          <Box sx={{ color: colors.accent, lineHeight: 0, mb: banner ? 0 : 2, flex: '0 0 auto' }}>
            <MarkIcon aria-hidden="true" sx={{ fontSize: iconSize }} />
          </Box>
          <Box>
            <LandingTitle
              component={titleComponent}
              sx={{ fontSize: kind === 'choice' ? '1.35rem' : '1.2rem', letterSpacing: '-0.02em' }}
            >
              {title}
            </LandingTitle>
            {label ? (
              <LandingLabel accent={colors.accent} sx={kind === 'price' ? { fontSize: '1.05rem', mt: 0.75 } : null}>
                {label}
              </LandingLabel>
            ) : null}
            {body ? (
              <LandingBody sx={{ maxWidth: banner ? 520 : 'none' }}>
                {body}
              </LandingBody>
            ) : null}
          </Box>
        </Box>
      )}
      {inline && label ? (
        <LandingLabel accent={colors.accent}>{label}</LandingLabel>
      ) : null}
      {inline && body ? (
        <LandingBody
          sx={kind === 'dilemma'
            ? { color: 'text.primary', fontSize: '1.05rem', lineHeight: 1.5, mt: 0.25, maxWidth: 'none' }
            : { maxWidth: 'none' }}
        >
          {body}
        </LandingBody>
      ) : null}
    </Box>
  );
};

const LandingTitle = ({ component, children, sx }) => (
  <Typography
    component={component}
    variant="subtitle1"
    sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3, ...sx }}
  >
    {children}
  </Typography>
);

const LandingLabel = ({ accent, children, sx }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, color: accent, mt: 0.25, ...sx }}>
    {children}
  </Typography>
);

const LandingBody = ({ children, sx }) => (
  <Typography variant="body1" sx={{ maxWidth: 560, mt: 0.75, color: 'text.secondary', ...sx }}>
    {children}
  </Typography>
);

export const LandingReveal = ({ children, index = 0, sx }) => {
  const reduceMotion = usePrefersReducedMotion();
  const animate = !reduceMotion && index < 8;

  return (
    <Box
      component={motion.div}
      initial={animate ? { opacity: 0, y: 8 } : false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -40px 0px' }}
      transition={{ duration: animate ? 0.22 : 0, delay: animate ? index * 0.04 : 0 }}
      sx={sx}
    >
      {children}
    </Box>
  );
};
